import { Big } from 'big.js';
import { DisplayData, TenantJobLine, TenantOrderLine } from '~/lib/model';
import BreFloorplanConfig from '~/tenants/bre/performable/floorplan/BreFloorplanConfig';
import BreJob from '~/tenants/bre/model/BreJob';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { match } from 'ts-pattern';
import { BreFloorplanPay, BreFloorplanRate } from '~/tenants/bre/performable/floorplan/BreFloorplanData';
import { bre_round } from '~/tenants/bre/performable/common';

export default class BreFloorplanJob extends BreJob<typeof BreFloorplanConfig> {
  get performable(): typeof BreFloorplanConfig {
    return BreFloorplanConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get isStandalone(): boolean {
    if (this.appointment) {
      return this.appointment?.jobs.length == 1;
    }

    // if job is not assigned to an appointment, we assume it will be scheduled with other jobs
    return !(
      this.orderContainsPhotos ||
      this.orderContainsAerial ||
      this.orderContainsCinematic ||
      this.orderContainsMatterport ||
      this.orderContainsTwilight
    );
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.RENTAL);
  }

  static rate(sqft: number, type: BreOrderType): Big {
    const { BASELINE, MIN_RATE, SQFT } = BreFloorplanRate[type];
    const value = bre_round(new Big(SQFT).times(sqft - 3000).add(BASELINE));

    return value.lt(MIN_RATE) ? new Big(MIN_RATE) : value;
  }

  static pay(sqft: number, type: BreOrderType): Big {
    return bre_round(BreFloorplanJob.rate(sqft, type).times('0.35'));
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreFloorplanConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
    };
  }

  info(): DisplayData[] {
    const info = super.info();

    if (this.metadata.interactive_tour) {
      info.push({
        name: 'Interactive Home Tour',
        provider: true,
        value: 'Yes',
      });
    }

    if (this.metadata.cad_file) {
      info.push({
        name: 'CAD File',
        provider: true,
        value: 'Yes',
      });
    }

    if (this.metadata.floorplan_3d) {
      info.push({
        name: '3D Floorplan',
        provider: true,
        value: 'Yes',
      });
    }

    if (this.metadata.gla_report) {
      info.push({
        name: 'GLA Report',
        provider: true,
        value: 'Yes',
      });
    }

    return info;
  }

  onsite(): number {
    if (this.sqft > 0) {
      return match(this.sqft)
        .when(
          (sqft) => sqft <= 3000,
          () => 10,
        )
        .when(
          (sqft) => sqft <= 5000,
          () => 15,
        )
        .when(
          (sqft) => sqft <= 8000,
          () => 25,
        )
        .otherwise(() => 30);
    }

    return 0;
  }

  revenueLines(): TenantOrderLine[] {
    const { type } = this.order.metadata;
    const { cad_file, floorplan_3d, gla_report, interactive_tour, rush } = this.metadata;
    const { CAD_FILE, FLOORPLAN_3D, GLA_REPORT, INTERACTIVE_TOUR, RUSH, STANDALONE } = BreFloorplanRate[type];
    const lines: TenantOrderLine[] = [];

    if (this.sqft > 0 && !this.buyer('free_floorplan')) {
      const rate = BreFloorplanJob.rate(this.sqft, type);
      const amount = this.isStandalone ? rate.plus(STANDALONE) : rate;

      lines.push({
        id: '2d_floorplan',
        description: `${this.isStandalone ? 'Standalone ' : ''}2D Floorplan`,
        amount,
        discountable: true,
      });
    }

    if (interactive_tour) {
      lines.push({
        id: 'interactive_tour',
        description: 'Interactive Home Tour',
        amount: new Big(INTERACTIVE_TOUR),
      });
    }

    if (cad_file) {
      lines.push({
        id: 'cad_file',
        description: 'CAD File',
        amount: new Big(CAD_FILE),
      });
    }

    if (floorplan_3d) {
      lines.push({
        id: 'floorplan_3d',
        description: '3D Floorplan',
        amount: new Big(FLOORPLAN_3D),
      });
    }

    if (gla_report) {
      lines.push({
        id: 'gla_report',
        description: 'GLA Report',
        amount: new Big(GLA_REPORT),
      });
    }

    if (rush) {
      lines.push({
        id: 'rush_fee',
        description: 'Before noon rush fee',
        amount: new Big(RUSH),
      });
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { type } = this.order.metadata;
    const { STANDALONE } = BreFloorplanPay[type];
    const lines: TenantJobLine[] = [];

    if (this.sqft > 0) {
      const pay = BreFloorplanJob.pay(this.sqft, type);

      lines.push({
        id: 'floorplan',
        description: `${this.isStandalone ? 'Standalone ' : ''}2D Floorplan`,
        amount: this.isStandalone ? pay.plus(STANDALONE) : pay,
      });
    }

    return lines;
  }
}
